import { useFormContext } from "react-hook-form";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { couponCodeAtom, userInfoAtom } from "src/atoms";
import { useMyServiceBill } from "src/hooks/api/bill/useMyServiceBill";
import { useProductFlags } from "src/hooks/recoil/useProductFlags";
import { createServiceInicisBill, updateCompanyName } from "src/service/pay";
import { notifyError } from "src/components/Common/Toast";
import { insertInicisModule } from "src/utils/pay/insertInicisModuleNew";
import { getProductFromSessionStorage } from "src/utils/storage/auth";
import { useTranslation } from "react-i18next";
import { useRouter } from "src/hooks/useRouter";

export interface IPayDataType {
  buyerEmail: string;
  buyerName: string;
  // couponCode: string;
  customerUid: string;
  discountPrice: number;
  mKey: string;
  merchantUid: string;
  mid: string;
  oid: string;
  price: string;
  signature: string;
  timestamp: string;
  couponCode: string;
}
interface IReactInicis {
  payData: IPayDataType;
  currency: string;
  isPurchase?: boolean;
  isCardChange?: boolean; // 결제 카드 교체 여부
  goodName: string;
  returnUrl?: string;
  // closeUrl: string;
  children: ReactNode;
}

// PC 결제수단 반환
const payServerText = (index: number) => {
  if (index === 0) {
    return "Card"; // 카드
  } else if (index === 1) {
    return "VBank"; // 무통장
  } else if (index === 2) {
    return "HPP"; // 핸드폰
  } else if (index === 3) {
    return "DirectBank"; // 계좌이체
  }
};

// Mobile 결제수단 반환
const payServerTextMb = (index: number) => {
  if (index === 0) {
    return "CARD"; // 카드
  } else if (index === 1) {
    return "VBANK"; // 무통장
  } else if (index === 2) {
    return "MOBILE"; // 핸드폰
  } else if (index === 3) {
    return "BANK"; // 계좌이체
  }
};

const ReactInicis = ({
  payData,
  currency,
  isPurchase,
  isCardChange = false,
  goodName,
  returnUrl = window.location.origin +
    "/eco-cloud/wapi/cloud/pay/service/returnUrl/" +
    payData?.oid +
    "/" +
    window.sessionStorage.getItem("product"),
  children,
}: IReactInicis) => {
  // const mobilePurchaseRef = useRef();
  // const [timestamp, setTimestamp] = useState(0);
  // const [oid, setOid] = useState(0);
  // const isTest = window.location.hostname !== "cloudportal.fasoo.com";
  const [resolvedReturnUrl, setResolvedReturnUrl] = useState<string>("");
  const { lang } = useRouter();
  const closeUrl = window.location.href;
  // 클릭 시점의 최신 값/검증 결과가 필요하므로 watch()로 구독하지 않고
  // getValues()/trigger()를 호출해 읽는다 (useCallback 클로저 stale 방지)
  const { getValues, trigger } = useFormContext();
  const { isOciProduct } = useProductFlags();
  const { myServiceBill } = useMyServiceBill({});
  // const [price, setPrice] = useState<string>(payData?.price);
  const [userInfo, setUserInfo] = useRecoilState(userInfoAtom);
  const [couponCodeInfo, setCouponCodeInfo] = useRecoilState(couponCodeAtom);
  useEffect(() => {
    if (payData?.oid) {
      setResolvedReturnUrl(
        window.location.origin +
          "/eco-cloud/wapi/cloud/pay/service/returnUrl/" +
          payData?.oid +
          "/" +
          window.sessionStorage.getItem("product")
      );
    }
  }, [payData?.oid]);
  useEffect(() => {
    insertInicisModule();
  }, []);
  // useEffect(() => {
  //   if (couponCodeInfo) {
  //     if (couponCodeInfo.discountType === "PERCENT") {
  //       console.log("여기야~", payData?.price, couponCodeInfo);
  //       setPrice(
  //         (
  //           Number(payData?.price) *
  //           (1 - Number(couponCodeInfo.discountPrice) / 100)
  //         ).toString()
  //       );
  //     } else if (couponCodeInfo.discountType === "AMOUNT") {
  //       setPrice(
  //         (
  //           Number(payData?.price) - Number(couponCodeInfo.discountPrice)
  //         ).toString()
  //       );
  //     }
  //   } else {
  //     setPrice(payData?.price);
  //   }
  // }, [couponCodeInfo, payData]);
  // 이니시스 결제창 오픈 — 모듈 로드를 보장한 뒤 호출한다
  const openInicisPayWindow = useCallback(async () => {
    try {
      await insertInicisModule();
      // @ts-expect-error 이니시스가 전역에 주입하는 모듈
      if (!window?.INIStdPay) {
        notifyError("결제 모듈이 정상적으로 로드되지 않았습니다.");
        return;
      }
      // @ts-expect-error 이니시스가 전역에 주입하는 모듈
      window.INIStdPay.pay("inicisForm", {
        success: function (data: any) {
          console.log("결제 성공", data);
        },
        error: function (data: any) {
          console.error("결제 실패", data);
        },
        close: function () {
          console.log("결제창 닫힘");
        },
      });
    } catch (err) {
      console.error("이니시스 모듈 로딩 실패", err);
      notifyError("결제 모듈 로딩 중 오류가 발생했습니다.");
    }
  }, []);

  // 이미 이용 중인 구독이 있어 신규 결제를 막아야 하는지 여부
  const hasBlockingSubscription = useCallback(() => {
    // 조회 전(undefined)에는 막지 않는다 — 기존 구독이 없는 경우와 동일하게 취급
    const bills: any[] = myServiceBill ?? [];
    if (bills.length > 1) {
      const endedCount = bills.filter(
        (bill: any) => bill.payStatus === "END"
      ).length;
      return bills.length !== endedCount + 1;
    }
    if (bills.length === 1) {
      return bills.some(
        (bill: any) =>
          bill.payStatus !== "END" &&
          (bill.billType === "PROMOTION" ||
            (bill.billType === "PAY" &&
              (bill.status === "LINK" || bill.status === "UNLINK")))
      );
    }
    return false;
  }, [myServiceBill]);

  // 구매하기 버튼 클릭
  const onClickPurchase = useCallback(async () => {
    if (!payData) return;

    // 카드 교체는 검증 대상 폼이 아니므로 건너뛴다.
    // 신규 결제는 검증을 통과하지 못하면 결제창 자체를 띄우지 않는다.
    if (!isCardChange && !(await trigger())) return;

    // 클릭 시점의 최신 폼 값 (렌더 시점 클로저를 사용하지 않음)
    const values = getValues();

    try {
      const res = await createServiceInicisBill({
        serviceBillUid: values?.serviceBillUid,
        userCode: userInfo.userCode,
        billType: "PAY",
        billCode: values?.priceUid, // price의 uid값 전달
        licenseCount: values?.licenseCount,
        orderID: payData?.oid,
        subscriptionID: "Inicis",
        payTerm: values?.payTerm,
        startTime: 0,
        oldSubscriptionID: "",
        currency,
        payCompany: "INICIS",
        referralId: values?.referralId,
        nxacAppUids: values?.selectAppList ?? [],
        billNewType: isCardChange ? "CHANGE" : "NEW",
        totalPrice: values?.totalPrice,
        discountPrice: payData?.discountPrice,
        couponCode: payData?.couponCode,
      });

      // 청구 정보 생성에 실패하면 결제창을 띄우지 않는다
      if (res?.status !== 200) {
        notifyError("결제 정보 생성에 실패했습니다. 잠시 후 다시 시도해주세요.");
        return;
      }

      if (!isCardChange) {
        const isWrapsodyProduct =
          getProductFromSessionStorage() === "WRAPSODY";
        if (isOciProduct && isWrapsodyProduct) {
          // 회사명 업데이트
          updateCompanyName({ companyName: values?.companyName });
        }
        // 중복 구독 차단 (OCI/WRAPSODY는 기존과 동일하게 예외)
        if (
          hasBlockingSubscription() &&
          !isOciProduct &&
          !isWrapsodyProduct
        ) {
          notifyError("subscriptionType.alreadyCreatedMsg");
          return;
        }
      }

      await openInicisPayWindow();
    } catch (error: any) {
      notifyError(
        error?.response?.data?.errorMessage ??
          "결제 요청 중 오류가 발생했습니다."
      );
    }
  }, [
    payData,
    isCardChange,
    currency,
    isOciProduct,
    userInfo.userCode,
    getValues,
    trigger,
    hasBlockingSubscription,
    openInicisPayWindow,
  ]);
  const { t } = useTranslation();
  const billPrint_msg = t(
    "siteManagement.paymentHistory.bill.detail.includeVat"
  );

  return (
    <>
      {/* 이니시스 PC 결제 폼 */}
      <form
        className="inicisForm"
        id="inicisForm"
        name="inicisForm"
        method="POST"
        style={{ display: "none" }}
      >
        <input name="version" readOnly value="1.0" />
        <input name="mid" readOnly value={payData?.mid} />
        {/* <input name="mid" readOnly value={"INIBillTst"} /> */}
        <input name="goodname" readOnly required={true} value={goodName} />
        <input name="oid" readOnly value={payData?.oid} />
        <input name="price" readOnly value={payData?.price} />
        <input name="currency" readOnly value={currency} />
        <input name="buyername" readOnly value={payData?.buyerName} />
        <input name="buyertel" readOnly value="010-0000-0000" />
        <input name="buyeremail" readOnly value={payData?.buyerEmail} />
        <input name="timestamp" readOnly value={payData?.timestamp} />
        <input name="signature" readOnly value={payData?.signature} />
        <input name="returnUrl" readOnly value={resolvedReturnUrl} />
        <input name="mKey" readOnly value={payData?.mKey} />
        <input name="gopaymethod" readOnly value="Card" />
        <input name="acceptmethod" readOnly value="below1000:billauth(card)" />
        <input name="billPrint_msg" readOnly value={billPrint_msg} />
        <input name="languageView" readOnly value={lang} />
        <input name="charset" readOnly value="UTF-8" />
        <input name="merchantData" readOnly value="" />
        <input name="tax" readOnly value="0" />
        <input name="taxfree" readOnly value="0" />
        <input type="hidden" readOnly name="closeUrl" value={closeUrl} />
      </form>

      {/* 이니시스 MOBILR 결제 폼 */}
      {/* <form
        name="mobileweb"
        method="post"
        acceptCharset="euc-kr"
        ref={mobilePurchaseRef}
      >
        <input
          type="text"
          readOnly
          name="P_NEXT_URL"
          value={payData.returnUrl}
        />
        <input
          type="text"
          readOnly
          name="P_INI_PAYMENT"
          value={payServerTextMb(payData.payStatus)}
        />
        <input
          type="text"
          readOnly
          name="P_MID"
          value={isTest ? "INIpayTest" : payData.mid}
        />
        <input type="text" readOnly name="P_OID" value={oid} />
        <input
          type="text"
          readOnly
          name="P_GOODS"
          value={payData.productName}
        />
        <input type="text" readOnly name="P_AMT" value={payData.productPrice} />
        <input type="text" readOnly name="P_UNAME" value={payData.buyerName} /> */}

      {/* 휴대폰결제 필수 [1:컨텐츠, 2:실물] */}
      {/* {payData.payStatus === 2 && (
          <input
            type="text"
            readOnly
            name="P_HPP_METHOD"
            value={payData.telStatus}
          />
        )} */}
      {/* </form> */}

      <span onClick={onClickPurchase}>{children}</span>
    </>
  );
};

export default ReactInicis;
